// 대균열 저장(dungeonhero.grift.v1, 새 진행 키 — 세대 3): 최고 단계·열쇠·열쇠 받은 날·10단계 이정표 수령·도전/클리어 수·최근 판 영수증.
// 필드 저장(field/save.ts)과 같은 규칙: 주 키 + 정상 사본(.good), 손상이면 원문을 .bak에 남기고 사본으로, 미래 버전은 읽기 전용, 쓰기는 고정 스텝 밖(flush).
// 캐릭터 단위(진행 키라 캐릭터별로 따로, 영웅별은 아님): 상한 식이 그 캐릭터의 전투력(회차·레벨·다른 영웅·숙련)으로 정해진다.
import type { SaveStorage } from '../save';
import { GRIFT_KEY_STOCK, GRIFT_MAX, griftKeysAt } from '../../shared/field/greaterRift';

export const GRIFT_SAVE_KEY = 'dungeonhero.grift.v1';
export const GRIFT_SAVE_VERSION = 1;
/** 보상 영수증(판 id) 최근 몇 개를 기억해 같은 판 보상이 두 번 들어가지 않게 한다 */
export const GRIFT_RECEIPTS = 16;

export interface GriftSaveData {
  v: number;
  /** 최고 단계(시간 안에 깬 가장 높은 단계) */
  best: number;
  keys: number;
  /** 마지막으로 열쇠를 계산한 KST 날짜 번호(dayIndex). -1 = 아직 없음(첫 계산 때 그날 몫을 준다) */
  day: number;
  /** 10단계 이정표 수령 비트(0번 비트 = 10단계 … 14번 = 150단계) */
  ms: number;
  tries: number;
  clears: number;
  /** 최근 보상 받은 판 id */
  rc: string[];
}
export type GriftSaveMode = 'ok' | 'recovered' | 'future' | 'unavailable';

export const freshGrift = (): GriftSaveData => ({ v: GRIFT_SAVE_VERSION, best: 0, keys: 0, day: -1, ms: 0, tries: 0, clears: 0, rc: [] });
const int = (v: unknown, lo: number, hi: number): number | null => (typeof v === 'number' && Number.isSafeInteger(v) && v >= lo && v <= hi ? v : null);

export function parseGriftSave(raw: string | null): GriftSaveData | 'future' | null {
  if (raw === null) return null;
  let o: unknown;
  try { o = JSON.parse(raw); } catch { return null; }
  if (!o || typeof o !== 'object' || Array.isArray(o)) return null;
  const r = o as Record<string, unknown>;
  const v = int(r.v, 1, 1e6);
  if (v === null) return null;
  if (v > GRIFT_SAVE_VERSION) return 'future';
  const best = int(r.best, 0, GRIFT_MAX), keys = int(r.keys, 0, GRIFT_KEY_STOCK), day = int(r.day, -1, 1e7);
  const ms = int(r.ms, 0, (1 << 15) - 1), tries = int(r.tries, 0, 1e9), clears = int(r.clears, 0, 1e9);
  if (best === null || keys === null || day === null || ms === null || tries === null || clears === null) return null;
  if (!Array.isArray(r.rc) || r.rc.length > GRIFT_RECEIPTS || !r.rc.every((x) => typeof x === 'string' && x.length > 0 && x.length <= 40)) return null;
  return { v, best, keys, day, ms, tries, clears, rc: r.rc as string[] };
}

/**
 * 다른 탭 저장본과 합치기(PR #343 리뷰 P1): 같은 캐릭터를 두 탭에서 열면 각 탭이 연 시점의 값만 들고 있다가 덮어써서
 * 최고 기록·이정표·영수증이 사라지고 같은 열쇠·판 id를 두 번 쓸 수 있었다. 영구 진행은 단조(최고·이정표 OR·횟수 최대·영수증 합집합),
 * 열쇠는 더 늦은 날의 값, 같은 날이면 적은 쪽(어느 탭이든 쓴 열쇠는 되살아나지 않는다).
 */
export function mergeGrift(a: GriftSaveData, b: GriftSaveData): GriftSaveData {
  const rc = b.rc.slice();
  for (const id of a.rc) if (!rc.includes(id)) rc.push(id);
  if (rc.length > GRIFT_RECEIPTS) rc.splice(0, rc.length - GRIFT_RECEIPTS);
  const kd = a.day === b.day ? { keys: Math.min(a.keys, b.keys), day: a.day } : a.day > b.day ? { keys: a.keys, day: a.day } : { keys: b.keys, day: b.day };
  return {
    v: Math.max(a.v, b.v), best: Math.max(a.best, b.best), keys: kd.keys, day: kd.day, ms: a.ms | b.ms,
    tries: Math.max(a.tries, b.tries), clears: Math.max(a.clears, b.clears), rc,
  };
}

export class GriftSave {
  data: GriftSaveData = freshGrift();
  mode: GriftSaveMode = 'ok';
  private dirty = false;
  constructor(private readonly storage: SaveStorage | null) { this.load(); }

  load(): void {
    const st = this.storage;
    if (!st) { this.mode = 'unavailable'; this.data = freshGrift(); return; }
    let raw: string | null;
    try { raw = st.getItem(GRIFT_SAVE_KEY); } catch { this.mode = 'unavailable'; return; }
    const p = parseGriftSave(raw);
    if (p === 'future') { this.mode = 'future'; this.data = freshGrift(); return; }
    if (p) { this.data = p; this.mode = 'ok'; return; }
    if (raw === null) { this.data = freshGrift(); this.mode = 'ok'; return; }
    try { st.setItem(GRIFT_SAVE_KEY + '.bak', raw); } catch { /* 보관 실패해도 이어 간다 */ }
    let good: GriftSaveData | 'future' | null = null;
    try { good = parseGriftSave(st.getItem(GRIFT_SAVE_KEY + '.good')); } catch { good = null; }
    this.data = good && good !== 'future' ? good : freshGrift();
    this.mode = 'recovered';
    this.dirty = true;
  }

  /**
   * 저장소의 최신 값(다른 탭이 쓴 것)을 받아 온다: 바꾼 게 없으면 그대로 받고, 있으면 mergeGrift로 합친다.
   * 정상 모드에서만(복구·미래·없음은 그대로). 읽기 실패·손상 원문이면 아무것도 하지 않는다(다음 flush가 이 탭 값으로 고친다)
   */
  pull(): void {
    if (this.mode !== 'ok' || !this.storage) return;
    let p: GriftSaveData | 'future' | null;
    try { p = parseGriftSave(this.storage.getItem(GRIFT_SAVE_KEY)); } catch { return; }
    if (p === 'future') { this.mode = 'future'; return; }
    if (!p) return;
    this.data = this.dirty ? mergeGrift(this.data, p) : p;
  }

  /** 오늘(KST 날짜 번호) 몫의 열쇠를 채운다. 첫 계산(day −1)은 그날 몫 2개 */
  refreshKeys(day: number): number {
    this.pull();
    const d = this.data;
    const next = d.day < 0 ? { keys: Math.min(GRIFT_KEY_STOCK, d.keys + 2), day } : griftKeysAt(d.keys, d.day, day);
    if (next.keys !== d.keys || next.day !== d.day) { d.keys = next.keys; d.day = next.day; this.dirty = true; }
    return d.keys;
  }
  /** 열쇠 하나를 쓴다(입장 때, 판 시작 전에 저장 — 새로 고침으로 되돌리지 못한다) */
  spendKey(): boolean {
    // 다른 탭이 쓴 열쇠·도전 수를 먼저 받아 오고, 쓴 즉시 저장한다(두 탭이 같은 열쇠·같은 판 id를 쓰지 않게 — 드문 사건이라 스텝 안 쓰기 허용)
    this.pull();
    if (this.data.keys <= 0) return false;
    this.data.keys--; this.data.tries++; this.dirty = true;
    this.flush();
    return true;
  }
  /** 판 보상을 이미 받았는가 */
  hasReceipt(id: string): boolean { return this.data.rc.includes(id); }
  /**
   * 클리어 기록(한 판에 한 번): 영수증을 남기고 최고 기록·클리어 수를 올린다. 같은 판 id면 아무것도 바꾸지 않고 false.
   * 반환: 새로 받은 10단계 이정표 단계 목록(이정표 비트도 같이 켠다)
   */
  recordClear(id: string, newBest: number): { ok: boolean; milestones: number[] } {
    this.pull();
    const d = this.data;
    if (this.hasReceipt(id)) return { ok: false, milestones: [] };
    d.rc.push(id); if (d.rc.length > GRIFT_RECEIPTS) d.rc.splice(0, d.rc.length - GRIFT_RECEIPTS);
    d.clears++;
    const milestones: number[] = [];
    const nb = Math.max(d.best, Math.min(GRIFT_MAX, newBest));
    for (let t = 10; t <= nb; t += 10) { const bit = 1 << (t / 10 - 1); if (!(d.ms & bit)) { d.ms |= bit; milestones.push(t); } }
    d.best = nb;
    this.dirty = true;
    return { ok: true, milestones };
  }
  /** 시간 초과 판도 영수증만 남긴다(위로 상자 한 번) */
  recordLate(id: string): boolean {
    this.pull();
    const d = this.data;
    if (this.hasReceipt(id)) return false;
    d.rc.push(id); if (d.rc.length > GRIFT_RECEIPTS) d.rc.splice(0, d.rc.length - GRIFT_RECEIPTS);
    this.dirty = true;
    return true;
  }

  flush(): boolean {
    if (!this.dirty) return true;
    if (this.mode === 'future' || this.mode === 'unavailable' || !this.storage) return false;
    this.pull();
    const raw = JSON.stringify(this.data);
    try {
      this.storage.setItem(GRIFT_SAVE_KEY, raw);
      this.storage.setItem(GRIFT_SAVE_KEY + '.good', raw);
    } catch { return false; }
    this.dirty = false;
    return true;
  }
}
